import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Typography from '@mui/material/Typography';
import { put } from '@nineeng/request';
import { Badge, DetailsTab, RequirementChip, Spinner } from '@repo/components';
import { LanguageCode } from '@repo/shared/domain';
import { EventEmitter, constructQuery } from '@repo/shared/helpers';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent, getEventForm } from 'src/api/admin/events';
import { Settings, LanguagesSettings } from '@repo/shared/domain';
import { getSettings } from 'src/api/admin/settings';
interface DetailsSearch {
	language?: string;
}

export const Route = createFileRoute('/_admin/events/$eventId/details')({
	validateSearch: (search: Record<string, unknown>): DetailsSearch => ({
		language: search.language as string | undefined,
	}),
	component: DetailsPage,
});

function DetailsPage() {
	const { eventId } = Route.useParams();
	const { language: searchLanguage } = Route.useSearch();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};

	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['event', eventId],
		queryFn: () => getEvent(eventId),
	});

	const formSlug = event?.type?.Form?.slug;
	const { isLoading: languagesSettingsLoading } = useQuery({
		queryKey: ['settings', Settings.Languages],
		queryFn: () => getSettings<LanguagesSettings>(Settings.Languages),
	});

	const { data: form, isLoading: formLoading } = useQuery({
		queryKey: ['event-form', formSlug, searchLanguage],
		queryFn: () => getEventForm(formSlug!, searchLanguage),
		enabled: !!formSlug,
	});

	const users = event?.users?.filter((user) => user.status === 'active');

	const handleDetailsUpdate = async (eventData: Record<string, unknown>) => {
		await put(`/api/events/${eventId}/details`, { ...eventData })
			.then(() => {
				EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			})
			.catch((error: { message?: string; body?: { message?: string } }) => {
				const errorMessage = error?.message || error?.body?.message || 'Error';
				EventEmitter.emit('toast', { type: 'error', message: errorMessage });
			});
		queryClient.invalidateQueries({ queryKey: ['event', eventId] });
	};

	const handleLanguageChange = (language: string) => {
		updateQueryParams({ language });
	};

	const isLoading = eventLoading || formLoading || configLoading || languagesSettingsLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!event) return null;

	const availableLanguages = form?.availableLanguages ?? [];
	const defaultLanguage = form?.language ?? 'en';
	const languages = [...new Set([...availableLanguages, defaultLanguage])] as LanguageCode[];
	const language = languages.find((l) => l === searchLanguage) || defaultLanguage;

	const printHeader = (
		<div className="flex gap-2">
			<div className="w-[300px] flex items-center">
				<img style={{ objectFit: 'contain', maxHeight: 70, width: '100%' }} src="/api/public/logo" />
			</div>
			<div className="flex-grow flex flex-row flex-wrap p-2">
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Event Name
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.name || '-'}
					</Typography>
					{featureFlags?.USES_SUBCONTRACTING && (
						<>
							<Typography variant="caption" sx={{ marginBottom: 1 }}>
								Project
							</Typography>
							<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
								{event?.project?.name ? <Badge key={event.project?.name}>{event.project?.name}</Badge> : '-'}
							</Typography>
						</>
					)}
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Type
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.type ? <RequirementChip object={event} /> : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Description
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.description || '-'}
					</Typography>
				</div>
				<div className="w-[30%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Start Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.startTime ? new Date(event?.startTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Finish Time
					</Typography>
					<Typography variant="subtitle2" sx={{ marginBottom: 1 }}>
						{event?.finishTime ? new Date(event?.finishTime).toLocaleString('en-GB') : '-'}
					</Typography>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Zones
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.zones?.length ? event?.zones?.map((r) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-[40%]">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Owners
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.admins?.length ? event?.admins?.map((r) => <Badge key={r.email}>{r.email}</Badge>) : '-'}
					</div>
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Companies
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{event?.groups?.length ? event?.groups?.map((r) => <Badge key={r.name}>{r.name}</Badge>) : '-'}
					</div>
				</div>
				<div className="w-full">
					<Typography variant="caption" sx={{ marginBottom: 1 }}>
						Users
					</Typography>
					<div className="min-h-[29px] flex flex-wrap gap-1">
						{users?.length ? users?.map((r) => <Badge key={r.email}>{`${r.firstName} ${r.lastName}`}</Badge>) : '-'}
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<DetailsTab
			data={{ ...event.eventData, eventId }}
			printHeader={printHeader}
			form={form}
			onSave={handleDetailsUpdate}
			onLanguageChange={handleLanguageChange}
			language={language}
			availableLanguages={languages}
		/>
	);
}
